/**
 * Utilitaires PKCE (Proof Key for Code Exchange) pour OAuth2
 * Utilisé pour sécuriser le flow Authorization Code dans les Single Page Applications
 */

/**
 * Génère un code verifier aléatoire pour PKCE
 * Longueur: 43-128 caractères
 * Caractères: A-Z, a-z, 0-9, -, ., _, ~
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Génère un code challenge à partir du verifier
 * Méthode: S256 (SHA-256)
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
}

/**
 * Encode un buffer en Base64 URL-safe
 * Remplace +/ par -_ et retire le padding =
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Décode une chaîne JWT (sans vérification de signature)
 * Utilisé uniquement pour extraire les claims localement
 */
export function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Vérifie si un token JWT est expiré
 */
export function isTokenExpired(token: string): boolean {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  // Ajouter une marge de 30 secondes pour éviter les problèmes de timing
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const margin = 30 * 1000; // 30 secondes

  return currentTime >= (expirationTime - margin);
}

/**
 * Extrait les rôles d'un token JWT Keycloak
 */
export function extractRolesFromToken(token: string): string[] {
  const decoded = parseJwt(token);
  if (!decoded) {
    return [];
  }

  // Keycloak stocke les rôles dans realm_access.roles
  if (decoded.realm_access && Array.isArray(decoded.realm_access.roles)) {
    return decoded.realm_access.roles;
  }

  // Fallback: chercher dans le claim "roles" directement
  if (Array.isArray(decoded.roles)) {
    return decoded.roles;
  }

  return [];
}

/**
 * Extrait les informations utilisateur d'un token JWT
 */
export function extractUserFromToken(token: string) {
  const decoded = parseJwt(token);
  if (!decoded) {
    return null;
  }

  return {
    id: decoded.sub || decoded.userId,
    username: decoded.preferred_username || decoded.username || decoded.sub,
    email: decoded.email,
    firstName: decoded.given_name,
    lastName: decoded.family_name,
    roles: extractRolesFromToken(token),
    emailVerified: decoded.email_verified,
  };
}
